import { buildExclusivityQueryParams } from "@faire/retailer-visitor-shared/hooks/exclusivity/consts";
import { EXCLUSIVITY_AVAILABILITY_STALE_TIME } from "@faire/retailer-visitor-shared/hooks/exclusivity/useExclusivityAvailability";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { useSettingBrandReviewPdpApiSplit } from "@faire/retailer-visitor-shared/settings/useSettingBrandReviewPdpApiSplit";
import { useSettingPrefetchPdpApis } from "@faire/retailer-visitor-shared/settings/useSettingFEPrefetchPdpApis";
import { PreloadedImageTracker } from "@faire/retailer-visitor-shared/views/product/preload/PreloadedImageTracker";
import {
  buildProductImageGridSrcSet,
  IMAGE_GRID_MAX_IMAGES_VISIBLE,
} from "@faire/retailer-visitor-shared/views/product/ProductImages/utils";
import { prefetchLeanProduct } from "@faire/retailer-visitor-shared/views/product/utils/useLeanProductQuery";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import {
  getQueryFn as getExclusivityAvailabilityQueryFn,
  getQueryKey as getExclusivityAvailabilityQueryKey,
} from "@faire/web-api--source/endpoints/www/api/exclusivity-agreements/retailers/retailerToken/availability/get-hooks";
import {
  getQueryKey as getProductReviewsQueryKey,
  getQueryFn as getProductReviewsQueryFn,
} from "@faire/web-api--source/endpoints/www/api/product/productToken/brand-reviews-summary/get-hooks";
import {
  getQueryKey as getProductPromotionsQueryKey,
  getQueryFn as getProductPromotionsQueryFn,
} from "@faire/web-api--source/endpoints/www/api/product/productToken/product-promo-messages/get-hooks";
import fetchCart from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get";
import { getQueryKey as getCartQueryKey } from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get-hooks";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { getCartQueryParams } from "../../brand/BrandCart/hooks/consts";
import { BRAND_CART_STALE_TIME } from "../../brand/BrandCart/hooks/useBrandCart";
import { BRAND_REVIEW_SUMMARY_STALE_TIME } from "../__internal__/hooks/useBrandReviewSummary";
import { PRODUCT_PROMOTIONS_STALE_TIME } from "../ProductDetails/AddToCart/useProductPromotionsState";

export interface ProductDetails {
  productToken: string | undefined;
  bestImage: IImage | undefined;
  images: Array<IImage>;
  showVerticalImagery?: boolean;
  brandToken: string | undefined;
}

export function usePreloadNavigationToPDP() {
  const queryClient = useQueryClient();
  const brandReviewPdpApiSplit = useSettingBrandReviewPdpApiSplit();
  const prefetchPdpApis = useSettingPrefetchPdpApis();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { retailerToken = "" } = useRetailer();
  return useCallback(
    (product: ProductDetails) => {
      preloadNavigationToPDP(
        queryClient,
        product,
        isLoggedInRetailer,
        prefetchPdpApis,
        brandReviewPdpApiSplit,
        retailerToken
      );
    },
    [
      queryClient,
      prefetchPdpApis,
      isLoggedInRetailer,
      brandReviewPdpApiSplit,
      retailerToken,
    ]
  );
}

function preloadNavigationToPDP(
  queryClient: QueryClient,
  product: ProductDetails,
  isLoggedInRetailer: boolean,
  prefetchPdpApis: boolean,
  brandReviewPdpApiSplit: boolean,
  retailerToken: string
): void {
  prefetchProductImages(product);

  if (product.productToken) {
    // Prefetch product details
    prefetchLeanProduct(queryClient, product.productToken);

    if (prefetchPdpApis) {
      // Prefetch product promotions
      queryClient.prefetchQuery({
        queryKey: getProductPromotionsQueryKey(product.productToken),
        queryFn: getProductPromotionsQueryFn(product.productToken),
        staleTime: PRODUCT_PROMOTIONS_STALE_TIME,
      });

      if (!brandReviewPdpApiSplit) {
        // Prefetch brand/product reviews
        queryClient.prefetchQuery({
          queryKey: getProductReviewsQueryKey(product.productToken),
          queryFn: getProductReviewsQueryFn(product.productToken),
          staleTime: BRAND_REVIEW_SUMMARY_STALE_TIME,
        });
      }
    }
  }

  if (prefetchPdpApis && product.brandToken && isLoggedInRetailer) {
    // Prefetch cart for logged in retailer
    const params = getCartQueryParams(product.brandToken);
    queryClient.prefetchQuery({
      queryKey: getCartQueryKey(getCartToken(), params),
      queryFn: () => {
        return fetchCart(getCartToken(), params);
      },
      staleTime: BRAND_CART_STALE_TIME,
    });

    // Prefetch exclusivity availability
    queryClient.prefetchQuery({
      queryKey: getExclusivityAvailabilityQueryKey(
        retailerToken,
        buildExclusivityQueryParams(product.brandToken)
      ),
      queryFn: getExclusivityAvailabilityQueryFn(
        retailerToken,
        buildExclusivityQueryParams(product.brandToken)
      ),
      staleTime: EXCLUSIVITY_AVAILABILITY_STALE_TIME,
    });
  }
}

function prefetchProductImages(product: ProductDetails): void {
  PreloadedImageTracker.get().clear();

  const { Image } = getWindowOrThrow();

  const showVerticalImagery = product.showVerticalImagery || false;

  getUniqueImages(product).forEach((image) => {
    const { src, srcSet } = buildProductImageGridSrcSet(
      image,
      showVerticalImagery
    );
    if (!src || !srcSet) {
      return;
    }

    const img = new Image();
    img.srcset = srcSet;
    img.src = src;

    PreloadedImageTracker.get().setInfo(src, {
      img,
      showVerticalImagery,
    });
  });
}

function getUniqueImages(product: ProductDetails): IImage[] {
  const images = product.images ? [...product.images] : [];
  const bestImage = product.bestImage;

  if (bestImage?.url && images.every((image) => image.url !== bestImage.url)) {
    images.unshift(bestImage);
  }

  return images.slice(0, IMAGE_GRID_MAX_IMAGES_VISIBLE);
}
